'use client';

import { UserIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'use-intl';
import Link from '@/components/atoms/link';
import useAccount from '@/lib/hooks/useAccount';
import useAuthDrawer from '../auth-drawer-provider/hooks/useAuthDrawer';
import { SIGN_IN_DRAWER_ID } from '../sign-in-drawer';

interface AccountNavButtonProps {
  /** Destination for authenticated users (e.g. /account dashboard). */
  accountHref: string;
  className?: string;
}

/**
 * Header user-icon affordance.
 *
 * Guest → opens the sign-in drawer via AuthDrawerProvider.
 * Logged-in → navigates to the account dashboard via the project's locale-
 * aware Link atom.
 *
 * Renders the icon identically in both states so the header chrome doesn't
 * shift when auth context resolves.
 */
const AccountNavButton = ({ accountHref, className = '' }: AccountNavButtonProps) => {
  const translate = useTranslations();
  const { loggedIn } = useAccount();
  const { mode, open } = useAuthDrawer();

  const wrapperClassName = `text-white hover:text-white/80 ${className}`.trim();
  const icon = <UserIcon className="size-6 lg:size-7" aria-hidden />;

  if (loggedIn) {
    return (
      <Link
        href={accountHref}
        className={wrapperClassName}
        underlineOnHover={false}
        aria-label={translate('common.account')}
      >
        {icon}
      </Link>
    );
  }

  const isDrawerOpen = mode === 'sign-in';

  return (
    <button
      type="button"
      onClick={() => open('sign-in')}
      aria-label={translate('common.account')}
      aria-haspopup="dialog"
      aria-controls={SIGN_IN_DRAWER_ID}
      aria-expanded={isDrawerOpen}
      className={wrapperClassName}
    >
      {icon}
    </button>
  );
};

export default AccountNavButton;
